import { CarerInfo } from "../response/core";
import { Device } from "./device.domain";
import { SchoolGroup } from "./group.domain";

export interface SchoolUser {
    schoolAccountId: string;
    uniqueIdentifierNumber: string;
    upi: string;
    misId: string;
    internalIdentifierCode: string;
    tutorGroup: string;
    yearGroup: string;
    userInfo: UserInfo;
    familyMembers: UserInfo[];
    devices?: Device[];
    schoolGroups?: SchoolGroup[];
    schoolUserType: SchoolUserType;
    schoolUserStatus: SchoolUserStatus;
    /** Set only while schoolUserStatus is EXEMPT. */
    exemptionReason?: SchoolUserExemptionReason;
    exemptionNote?: string;
}

export interface UserInfo {
    accountId: string;
    profileId: string;
    accountType: AccountType;
    status: AccountStatus;
    userAuthenticationId?: string;
    family?: FamilyAccount;
    carerFamilies?: MonitoredFamily[];
    firstname: string;
    lastname: string;
    gender?: GenderType; 
    dateOfBirth?: Date;
    emailAddress?: string;
    country?: string;
    phoneNumber?: string;
    timezone: string;
    theme?: Theme;
    avatar?: string;
    creationDatetime?: Date;
    lastUpdateDatetime?: Date;
    subscribedToMessages?: boolean;
    testAccount?: boolean;
}

export interface UserProfile {
    accountId: string;
    lastName?: string;
    firstName?: string;
    gender?: GenderType;
    dateOfBirth?: Date;
    emailAddress?: string;
    e164phoneNumber?: string;
    avatar?: string;
}

/** Year groups only, classes only, or both (DIGI-1851). */
export type SyncMode = 'YEAR_GROUP' | 'CLASSES' | 'MIXED';

export interface School {
    accountId: string;
    wondeId: string,
    urn: string,
    mis: string,
    profileId: string;
    identifierCode: string;
    name: string;
    phoneNumbers?: string[];
    /** Account ids of active staff who receive school notices (e.g. a parent cancelling a school task). */
    notificationRecipientIds?: string[];
    /** Sign-up admin email(s): the default notice recipient until staff are selected. */
    adminEmailAddresses?: string[];
    /** School photo as a data URL; absent when the school has not set one. */
    logo?: string;
    status: AccountStatus;
    integrationStatus?: SchoolIntegrationStatus;
    lastIntegrationStatusUpdatedDatetime?: Date;
    lastSyncDatetime?: Date;
    /** How the MIS sync uses the school's data; absent until an admin chooses (DIGI-1851). */
    syncMode?: SyncMode;
    timezone: string;
    address?: Address;
    createdBy: string;
    creationDatetime?: Date;
    lastModifiedDatetime?: Date;
}

export interface MonitoredFamily {
    id: string;
    accountId: string; 
    familyId: string; 
    firstname: string;
    lastname: string;
    phoneNumber?: string;
    emailAddress?: string;
    createdBy: string; // AccountId
    validityStartDate?: Date; 
    validityEndDate?: Date; 
    creationDatetime: Date; 
}

export interface FamilyAccount {
    id: string;
    status: FamilyStatus; 
    createdBy: string; 
    membershipInfo?: Membership;
    creationDatetime: Date; 
    lastModifiedDatetime: Date; 
}

export interface Membership {
    type: MembershipType; 
    paymentMethod?: PaymentMethod
    startDate: Date; 
    expiryDate?: Date; 
    subscribedBy: string; // AccountId
}

export interface PaymentMethod {
    type: PaymentMethodType; 
}
export interface Address {
    line1: string;
    line2: string;
    town: string;
    postCode: string;
    countryCode: string;
    countryName: string;
    countryIso31662: string;
    creationDatetime: Date;
    lastModifiedDatetime: Date;
}

export interface Theme {
    colorCategory: string;
    avatar: Avatar;
}

export interface Avatar {
    colorType: string;
    faceAndEyesSvg: string;
    hairSvg: string;
    mouthSvg: string;
}

export enum SchoolUserType {
    TEACHER = "TEACHER",
    
    HEAD_TEACHER = "HEAD_TEACHER",
    TEACHER_ASSISTANT = "TEACHER_ASSISTANT",
    TUTOR = "TUTOR",
    PUPIL = "PUPIL",
    ADMINISTRATOR = "ADMINISTRATOR",
}

export enum SchoolUserStatus {
    PENDING = "PENDING",
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE",
    /** The school has excused this pupil from LOQT; school restrictions never reach their device. */
    EXEMPT = "EXEMPT"
}

export enum SchoolUserExemptionReason {
    MEDICAL_REQUIREMENTS = "MEDICAL_REQUIREMENTS",
    NO_SMARTPHONE = "NO_SMARTPHONE",
    BASIC_PHONE = "BASIC_PHONE",
    /** The family says it uses another parental control app (DIGI-1865). Nothing checks that app. */
    DIFFERENT_APP = "DIFFERENT_APP",
    OTHER = "OTHER"
}

/** Labels shown in the UI. There is no i18n layer in this app, so these live with the enum. */
export const SCHOOL_USER_EXEMPTION_REASON_LABELS: Record<SchoolUserExemptionReason, string> = {
    [SchoolUserExemptionReason.MEDICAL_REQUIREMENTS]: "Medical requirements",
    [SchoolUserExemptionReason.NO_SMARTPHONE]: "No smartphone",
    [SchoolUserExemptionReason.BASIC_PHONE]: "Basic phone",
    [SchoolUserExemptionReason.DIFFERENT_APP]: "Different app",
    [SchoolUserExemptionReason.OTHER]: "Other"
};

/** Shown wherever "Different app" is chosen or displayed, so the school never reads it as LOQT coverage. */
export const DIFFERENT_APP_EXEMPTION_CAVEAT = "The family says it uses another app. LOQT does not check that app.";

/** Same rule as the backend's SchoolUserExemptionReason.takesNote(): only these reasons keep a free-text note. */
export function exemptionReasonTakesNote(reason: SchoolUserExemptionReason | string | null | undefined): boolean {
    return reason === SchoolUserExemptionReason.OTHER || reason === SchoolUserExemptionReason.DIFFERENT_APP;
}

export enum FamilyStatus {
    PENDING = "PENDING",
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE"
}

export enum MembershipType {
    IN_TRIAL = "IN_TRIAL", 
    FREEMIUM = "FREEMIUM", 
    PREMIUM = "PREMIUM",
}

export enum PaymentMethodType {
    APPLE_IN_APP_PAY = "APPLE_IN_APP_PAY",
    APPLE_PAY = "APPLE_PAY",
    CREDIT_DEBIT_CARD = "CREDIT_DEBIT_CARD",
    GOOGLE_PAY = "GOOGLE_PAY",
    PAYPAL = "PAYPAL",
}

export enum AccountType {
    PARENT = "PARENT",
    YOUTH = "YOUTH",
    CARER = "CARER",
}

export enum AccountStatus {
    PENDING = "PENDING",
    ACTIVE = "ACTIVE",
    INACTIVE_PENDING = "INACTIVE_PENDING",
    INACTIVE = "INACTIVE"
}

export enum GenderType {
    MALE = "MALE",
    FEMALE = "FEMALE",
    PREFER_NOT_TO_SAY = "PREFER_NOT_TO_SAY",
    OTHER = "OTHER"
}

export enum SchoolIntegrationStatus {
    NOT_LINKED = "Not linked",
    NOT_REQUESTED = "Not Requested",
    WAITING_FOR_APPROVAL = "Waiting for Approval",
    READY_TO_SYNCHRONIZE = "Ready to Synchronize",
    SYNCHRONIZED = "Synchronized",
    PARTIALLY_SYNCHRONIZED = "Partially Synchronized",
    ERROR = "Error / Action Required"
}